import type { FC } from "react";
import {
  describeReturnRequestCompletion,
  describeReturnRequestCompletionNote,
  RETURN_REQUEST_STATUS_LABEL_JA,
} from "../../../../libs/models/returns/customerLabels";
import { computeMypageReturnActions } from "../../../../libs/mypage/returnRequestActions";
import type { OrderDetailDTO } from "../../components/format";
import { Notice } from "../../components/Notice";
import { SectionHeading, SectionHeadingLink } from "../../components/SectionHeading";

/**
 * 注文詳細画面の「返品のお手続き状況」 カード。進行中と完了のどちらも同じカードで出す。
 *
 * 見出しは進み具合に依らず中立にしてある。「〜を承っています」のような受付を表す言い方は
 * 「これから申請を受け付ける」と読めるため使わない。今どの段階かはステータス行で示す。
 *
 * 表示要素:
 *  - ヘッダ: 申請番号 (code、仮コード時は省略) + 顧客向けステータスラベル
 *    (完了なら `describeReturnRequestCompletion` = 返金 / 交換品の手配のどちらが終わったか。
 *    返し方が顧客の予想と違う場合は `describeReturnRequestCompletionNote` の補足を小さく添える)
 *  - 事務手数料の入金の反映待ち (お願いではなく状況のお知らせ)。返送・口座・事務手数料のお支払いの
 *    お願いは注文詳細の最上部「ご対応のお願い」に出すので、ここでは重ねない
 *  - 見出しの右の「申請の詳細を確認する」 リンク (= 顧客マイページ `/my-returns/<uuid>` へ。他の欄の
 *    見出しの右のリンクと同じ形。詳細ロジック
 *    (実 BankForm / 手数料決済 URL) はそちらに集約済なので、本カードからは個別 CTA を出さず
 *    1 つのリンクで誘導する)
 *
 */
export const ReturnRequestStatusCard: FC<{ order: OrderDetailDTO }> = ({ order }) => {
  const active = order.activeReturnRequest;
  if (!active) return null;

  const feePending = computeMypageReturnActions(active).some(
    (a) => a.kind === "handling_fee_pending",
  );
  const completed = active.phase === "completed";
  const statusLabel = completed
    ? describeReturnRequestCompletion(active)
    : RETURN_REQUEST_STATUS_LABEL_JA[active.status];
  // 返し方が顧客の予想と違う場合だけ添える補足 (お支払い前の注文の請求取り消し等)
  const completionNote = completed ? describeReturnRequestCompletionNote(active) : null;

  return (
    <section className="mb-10">
      <SectionHeading
        title="返品のお手続き状況"
        meta={
          active.customerPageUrl ? (
            <SectionHeadingLink href={active.customerPageUrl}>
              申請の詳細を確認する
            </SectionHeadingLink>
          ) : undefined
        }
      />
      <div className="space-y-3 py-4 text-sm">
        <dl className="space-y-1 text-neutral-700">
          {active.code && (
            <div className="flex items-baseline gap-2">
              <dt className="w-24 text-neutral-500 text-xs">申請番号</dt>
              <dd className="font-medium text-neutral-900">{active.code}</dd>
            </div>
          )}
          <div className="flex items-baseline gap-2">
            <dt className="w-24 text-neutral-500 text-xs">ステータス</dt>
            <dd className="text-neutral-900">{statusLabel}</dd>
          </div>
        </dl>

        {completionNote && (
          <p className="text-neutral-500 text-xs leading-relaxed">{completionNote}</p>
        )}

        {feePending && (
          <Notice variant="info" title="お支払い情報を反映しています">
            <p>
              事務手数料のお支払い情報を反映しています。コンビニ払い等の場合、入金確認に
              数日いただく場合があります。
            </p>
          </Notice>
        )}

        {!active.customerPageUrl && (
          // Requested の段階 (= 顧客マイページ未発行) は受付確認メールを送付している旨だけ案内
          <p className="text-neutral-500 text-xs leading-relaxed">
            申請の受付確認メールを登録のメールアドレス宛にお送りしています。
          </p>
        )}
      </div>
    </section>
  );
};
